import { palette } from '../../styles/tokens';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  radius?: number;
  style?: React.CSSProperties;
}

export function Skeleton({ width = '100%', height = 16, radius = 6, style }: SkeletonProps) {
  return (
    <>
      <style>{`
        @keyframes crewbit-shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
      `}</style>
      <div style={{
        width, height, borderRadius: radius,
        background: `linear-gradient(90deg, ${palette.g100} 25%, ${palette.g150} 50%, ${palette.g100} 75%)`,
        backgroundSize: '800px 100%',
        animation: 'crewbit-shimmer 1.5s ease infinite',
        ...style,
      }} />
    </>
  );
}

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

export function TableSkeleton({ rows = 5, columns = 6 }: TableSkeletonProps) {
  return (
    <div style={{ padding: '12px 0' }}>
      {/* Header row */}
      <div style={{ display: 'flex', gap: 12, padding: '8px 12px', marginBottom: 8 }}>
        {Array.from({ length: columns }, (_, i) => (
          <Skeleton key={i} height={10} width={i === 0 ? 120 : 80} radius={4} />
        ))}
      </div>
      {/* Data rows */}
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} style={{ display: 'flex', gap: 12, padding: '10px 12px', borderTop: `1px solid ${palette.g100}` }}>
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} height={14} width={c === 0 ? 140 : `${60 + Math.random() * 40}%`} radius={4} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div>
      <Skeleton width={200} height={24} style={{ marginBottom: 6 }} />
      <Skeleton width={300} height={14} style={{ marginBottom: 24 }} />
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{ flex: 1, padding: '18px 20px', background: palette.white, borderRadius: 10, border: `1px solid ${palette.g150}` }}>
            <Skeleton width={80} height={10} style={{ marginBottom: 8 }} />
            <Skeleton width={100} height={24} />
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {[1, 2].map(i => (
          <div key={i} style={{ background: palette.white, borderRadius: 10, border: `1px solid ${palette.g150}`, padding: '16px 20px' }}>
            <Skeleton width={120} height={14} style={{ marginBottom: 16 }} />
            <Skeleton height={120} style={{ marginBottom: 8 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScheduleSkeleton() {
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Skeleton width={32} height={32} radius={6} />
        <Skeleton width={32} height={32} radius={6} />
        <Skeleton width={180} height={20} style={{ marginLeft: 4, marginTop: 6 }} />
      </div>
      <div style={{ background: palette.white, borderRadius: 10, border: `1px solid ${palette.g150}`, padding: '12px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Skeleton width={160} height={40} />
          {Array.from({ length: 7 }, (_, i) => <Skeleton key={i} height={40} style={{ flex: 1 }} />)}
        </div>
        {Array.from({ length: 5 }, (_, r) => (
          <div key={r} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Skeleton width={160} height={60} />
            {Array.from({ length: 7 }, (_, c) => <Skeleton key={c} height={60} style={{ flex: 1 }} />)}
          </div>
        ))}
      </div>
    </div>
  );
}
